import * as XLSX from 'xlsx-js-style';
import { toast } from "@/components/ui/use-toast";

const HEADER_STYLE = {
  font: { bold: true, color: { rgb: "FFFFFF" }, name: "Calibri", sz: 11 },
  fill: { patternType: "solid", fgColor: { rgb: "1F4E78" } },
  alignment: { horizontal: "left", vertical: "center" },
  border: {
    top: { style: "thin", color: { rgb: "FFFFFF" } },
    bottom: { style: "thin", color: { rgb: "FFFFFF" } },
    left: { style: "thin", color: { rgb: "FFFFFF" } },
    right: { style: "thin", color: { rgb: "FFFFFF" } },
  },
};

const applyHeaderStyle = (ws: XLSX.WorkSheet, headerLen: number) => {
  for (let c = 0; c < headerLen; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[addr]) ws[addr].s = HEADER_STYLE;
  }
};

interface PrimaveraTask {
  id?: string;
  taskId?: string;
  name: string;
  phase?: string;
  startDate?: string;
  endDate?: string;
  duration: number;
  progress?: number;
  dependencies?: string | string[];
}

interface PrimaveraProject {
  name: string;
  tasks?: PrimaveraTask[];
}

interface TaskNameToUuidMap {
  [taskName: string]: string;
}

interface PrimaveraAssignment {
  task_id: string;
  resource_id: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
  rate?: number | null;
  allocation_hrs?: string | null;
  allocated_percentage?: number | null;
}

interface PrimaveraSubtask {
  id: string;
  task_id: string;
  subtask_name: string;
  status?: string;
  estimated_hours?: number;
  actual_hours?: number;
  start_date?: string;
  end_date?: string;
  step?: number;
  step_name?: string;
  resource_id?: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
}

interface ActivityRecord {
  taskUniqueId: number;
  taskCode: string;
  taskName: string;
  wbsId: string;
  status: string;
  targetDurationHours: number;
  remainingDurationHours: number;
  targetWorkQty: number;
  remainWorkQty: number;
  actualWorkQty: number;
  targetStartDate: string;
  targetEndDate: string;
  actStartDate: string;
  actEndDate: string;
}

const HOURS_PER_DAY = 8;

const deriveProjectCode = (projectName: string): string => {
  if (!projectName) return "PRJ";
  const words = projectName
    .replace(/[^A-Za-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "PRJ";
  const initials = words.slice(0, 3).map((word) => word.charAt(0).toUpperCase()).join('');
  return initials || "PRJ";
};

const buildWbsShortName = (name: string, fallbackIndex: number): string => {
  const compact = (name || '')
    .replace(/[^A-Za-z0-9\s-]/g, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 3)
    .map((part) => part.slice(0, 8))
    .join('-');

  return compact || `WBS-${fallbackIndex + 1}`;
};

const sortSubtasks = (items: PrimaveraSubtask[]): PrimaveraSubtask[] =>
  [...items].sort((a, b) => {
    const stepDelta = (a.step || 0) - (b.step || 0);
    if (stepDelta !== 0) return stepDelta;

    const stepNameDelta = (a.step_name || '').localeCompare(b.step_name || '');
    if (stepNameDelta !== 0) return stepNameDelta;

    const startA = a.start_date || '';
    const startB = b.start_date || '';
    if (startA !== startB) return startA.localeCompare(startB);

    return (a.subtask_name || '').localeCompare(b.subtask_name || '');
  });

const round2 = (n: number) => Math.round(n * 100) / 100;

const parseDate = (value?: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const pad = (value: number) => String(value).padStart(2, '0');

const formatPrimaveraDate = (value?: string | Date | null): string => {
  const date = value instanceof Date ? value : parseDate(value || undefined);
  if (!date) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours() || 8)}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const addHours = (date: Date, hours: number): Date => new Date(date.getTime() + Math.max(0, hours) * 60 * 60 * 1000);

const statusFromProgress = (progress?: number): string => {
  const value = Math.max(0, Math.min(100, Math.round(progress || 0)));
  if (value >= 100) return 'Completed';
  if (value > 0) return 'In Progress';
  return 'Not Started';
};

const statusFromSubtaskStatus = (status?: string): string => {
  const value = (status || '').toLowerCase();
  if (value === 'done') return 'Completed';
  if (value === 'in progress') return 'In Progress';
  return 'Not Started';
};

const progressFromStatusLabel = (status: string): number => {
  if (status === 'Completed') return 100;
  if (status === 'In Progress') return 50;
  return 0;
};

const subtaskProgress = (status?: string): number => progressFromStatusLabel(statusFromSubtaskStatus(status));

const normalizeDates = (
  explicitStart: Date | null,
  explicitEnd: Date | null,
  fallbackStart: Date,
  fallbackDurationHours: number
) => {
  const safeStart = explicitStart || fallbackStart;
  const safeEnd = explicitEnd || addHours(safeStart, fallbackDurationHours || HOURS_PER_DAY);

  if (safeEnd.getTime() < safeStart.getTime()) {
    return {
      start: safeEnd,
      end: safeStart,
    };
  }

  return {
    start: safeStart,
    end: safeEnd,
  };
};

const getTaskLookupKeys = (
  task: PrimaveraTask,
  index: number,
  taskNameToUuid: TaskNameToUuidMap
): string[] => {
  const keys = [
    task.id,
    task.taskId,
    task.name,
    task.name ? taskNameToUuid[task.name] : undefined,
    String(index + 1),
  ].filter((value): value is string => Boolean(value));

  return [...new Set(keys)];
};

const parseDependencies = (dependencies?: string | string[]): string[] => {
  if (!dependencies) return [];
  if (Array.isArray(dependencies)) {
    return dependencies.map((value) => value?.trim()).filter(Boolean) as string[];
  }

  return dependencies
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
};

export const exportToPrimaveraXLSX = (
  project: PrimaveraProject,
  assignments: PrimaveraAssignment[] = [],
  subtasks: PrimaveraSubtask[] = [],
  taskNameToUuid: TaskNameToUuidMap = {}
) => {
  if (!project?.tasks?.length) {
    toast({ title: "Export Error", description: "No task data available for export", variant: "destructive" });
    return;
  }

  try {
    const wb = XLSX.utils.book_new();
    const projectCode = deriveProjectCode(project.name);
    const sortedSubtasks = sortSubtasks(subtasks);
    const projectStart = parseDate(project.tasks[0]?.startDate) || new Date();

    const taskLookupToParentKey = new Map<string, string>();
    const subtasksByParentKey = new Map<string, PrimaveraSubtask[]>();
    const parentTaskByKey = new Map<string, PrimaveraTask>();

    project.tasks.forEach((task, index) => {
      const parentKey = `PARENT_${index + 1}`;
      parentTaskByKey.set(parentKey, task);
      getTaskLookupKeys(task, index, taskNameToUuid).forEach((lookupKey) => taskLookupToParentKey.set(lookupKey, parentKey));
    });

    sortedSubtasks.forEach((subtask) => {
      const parentKey = taskLookupToParentKey.get(subtask.task_id);
      if (!parentKey) return;
      const existing = subtasksByParentKey.get(parentKey) || [];
      existing.push(subtask);
      subtasksByParentKey.set(parentKey, existing);
    });

    const wbsHeader = ["proj_id", "wbs_id", "parent_wbs_id", "wbs_name", "wbs_short_name", "seq_num"];
    const wbsRows: any[][] = [];
    const rootWbsId = `${projectCode}_ROOT`;
    let wbsSequence = 1;

    wbsRows.push([projectCode, rootWbsId, '', project.name, projectCode, wbsSequence++]);

    const taskHeader = [
      "proj_id",
      "task_id",
      "task_code",
      "task_name",
      "task_type",
      "status_code",
      "wbs_id",
      "clndr_id",
      "target_drtn_hr_cnt",
      "remain_drtn_hr_cnt",
      "target_work_qty",
      "remain_work_qty",
      "act_work_qty",
      "complete_pct_type",
      "target_start_date",
      "target_end_date",
      "act_start_date",
      "act_end_date",
    ];
    const taskRows: any[][] = [];

    const predHeader = ["task_pred_id", "proj_id", "pred_proj_id", "pred_task_id", "task_id", "pred_type", "lag_hr_cnt"];
    const predRows: any[][] = [];

    const rsrcHeader = ["rsrc_id", "rsrc_name", "rsrc_short_name", "rsrc_type"];
    const rsrcRows: any[][] = [];

    const trHeader = [
      "taskrsrc_id",
      "proj_id",
      "task_id",
      "rsrc_id",
      "target_qty",
      "remain_qty",
      "act_reg_qty",
      "target_cost",
      "remain_cost",
      "act_reg_cost",
      "target_start_date",
      "target_end_date",
    ];
    const trRows: any[][] = [];

    const parentFirstActivityId = new Map<string, number>();
    const parentLastActivityId = new Map<string, number>();
    const parentAssignmentAnchorId = new Map<string, number>();
    const subtaskActivityIdBySubtaskId = new Map<string, number>();
    const relationshipKeys = new Set<string>();

    let taskUniqueId = 1000;
    let relationshipUniqueId = 1;
    let taskResourceUniqueId = 1;

    const createActivityRecord = (
      taskCode: string,
      taskName: string,
      wbsId: string,
      status: string,
      targetDurationHours: number,
      progress: number,
      startDate: Date,
      endDate: Date
    ): ActivityRecord => {
      const safeTargetDuration = Math.max(0, Math.round(targetDurationHours));
      const safeProgress = Math.max(0, Math.min(100, Math.round(progress)));
      const remainingDuration = Math.round(safeTargetDuration * (1 - safeProgress / 100));
      const actualWorkQty = Math.round(safeTargetDuration - remainingDuration);

      return {
        taskUniqueId: taskUniqueId++,
        taskCode,
        taskName,
        wbsId,
        status,
        targetDurationHours: safeTargetDuration,
        remainingDurationHours: remainingDuration,
        targetWorkQty: safeTargetDuration,
        remainWorkQty: remainingDuration,
        actualWorkQty,
        targetStartDate: formatPrimaveraDate(startDate),
        targetEndDate: formatPrimaveraDate(endDate),
        actStartDate: safeProgress > 0 ? formatPrimaveraDate(startDate) : '',
        actEndDate: safeProgress >= 100 ? formatPrimaveraDate(endDate) : '',
      };
    };

    const appendActivityRow = (activity: ActivityRecord) => {
      taskRows.push([
        projectCode,
        activity.taskUniqueId,
        activity.taskCode,
        activity.taskName,
        'TT_Task',
        activity.status,
        activity.wbsId,
        'Standard',
        activity.targetDurationHours,
        activity.remainingDurationHours,
        activity.targetWorkQty,
        activity.remainWorkQty,
        activity.actualWorkQty,
        'Duration',
        activity.targetStartDate,
        activity.targetEndDate,
        activity.actStartDate,
        activity.actEndDate,
      ]);
    };

    const appendRelationship = (predTaskId?: number, succTaskId?: number) => {
      if (!predTaskId || !succTaskId || predTaskId === succTaskId) return;
      const key = `${predTaskId}->${succTaskId}`;
      if (relationshipKeys.has(key)) return;
      relationshipKeys.add(key);
      predRows.push([relationshipUniqueId++, projectCode, projectCode, predTaskId, succTaskId, 'FS', 0]);
    };

    project.tasks.forEach((task, index) => {
      const parentKey = `PARENT_${index + 1}`;
      const parentWbsId = `${projectCode}_WBS_${index + 1}`;
      const parentWbsCode = `${index + 1}`;
      const relatedSubtasks = subtasksByParentKey.get(parentKey) || [];

      wbsRows.push([
        projectCode,
        parentWbsId,
        rootWbsId,
        task.name,
        parentWbsCode,
        wbsSequence++,
      ]);

      if (relatedSubtasks.length === 0) {
        const fallbackDuration = Math.max(0, Math.round((task.duration || 0) * HOURS_PER_DAY));
        const normalized = normalizeDates(
          parseDate(task.startDate),
          parseDate(task.endDate),
          projectStart,
          fallbackDuration || HOURS_PER_DAY
        );
        const activity = createActivityRecord(
          `A${1000 + index * 10}`,
          task.name,
          parentWbsId,
          statusFromProgress(task.progress),
          fallbackDuration,
          Math.max(0, Math.min(100, Math.round(task.progress || 0))),
          normalized.start,
          normalized.end
        );

        appendActivityRow(activity);
        parentFirstActivityId.set(parentKey, activity.taskUniqueId);
        parentLastActivityId.set(parentKey, activity.taskUniqueId);
        parentAssignmentAnchorId.set(parentKey, activity.taskUniqueId);
        return;
      }

      const stepGroups = new Map<string, { label: string; stepNumber: number; items: PrimaveraSubtask[] }>();

      relatedSubtasks.forEach((subtask) => {
        const derivedLabel = (subtask.step_name || '').trim() || (subtask.step ? `Step ${subtask.step}` : 'Activities');
        const groupKey = (subtask.step || derivedLabel || 'direct').toString();
        const existing = stepGroups.get(groupKey) || {
          label: derivedLabel,
          stepNumber: subtask.step || 0,
          items: [],
        };
        existing.items.push(subtask);
        stepGroups.set(groupKey, existing);
      });

      const orderedGroups = [...stepGroups.values()].sort((a, b) => {
        const stepDelta = a.stepNumber - b.stepNumber;
        if (stepDelta !== 0) return stepDelta;
        return a.label.localeCompare(b.label);
      });

      const useNestedWbs = orderedGroups.length > 1 || orderedGroups.some((group) => group.label !== 'Activities');
      let firstTaskIdForParent: number | undefined;
      let lastTaskIdForParent: number | undefined;
      let anchorTaskIdForParent: number | undefined;

      orderedGroups.forEach((group, groupIndex) => {
        const groupWbsId = useNestedWbs ? `${parentWbsId}_${groupIndex + 1}` : parentWbsId;

        if (useNestedWbs) {
          wbsRows.push([
            projectCode,
            groupWbsId,
            parentWbsId,
            group.label,
            `${parentWbsCode}.${groupIndex + 1}`,
            wbsSequence++,
          ]);
        }

        const groupActivityIds: number[] = [];
        group.items.forEach((subtask, subtaskIndex) => {
          const baseDate = parseDate(subtask.start_date) || parseDate(task.startDate) || projectStart;
          const targetDuration = Math.max(0, Math.round(Number(subtask.estimated_hours) || 0));
          const normalized = normalizeDates(
            parseDate(subtask.start_date),
            parseDate(subtask.end_date),
            baseDate,
            targetDuration || HOURS_PER_DAY
          );
          const activity = createActivityRecord(
            `A${1000 + index * 10}_${String(groupIndex + 1).padStart(2, '0')}${String(subtaskIndex + 1).padStart(2, '0')}`,
            subtask.subtask_name,
            groupWbsId,
            statusFromSubtaskStatus(subtask.status),
            targetDuration,
            subtaskProgress(subtask.status),
            normalized.start,
            normalized.end
          );

          appendActivityRow(activity);
          groupActivityIds.push(activity.taskUniqueId);
          subtaskActivityIdBySubtaskId.set(subtask.id, activity.taskUniqueId);

          if (!firstTaskIdForParent) firstTaskIdForParent = activity.taskUniqueId;
          if (!anchorTaskIdForParent) anchorTaskIdForParent = activity.taskUniqueId;
          lastTaskIdForParent = activity.taskUniqueId;
        });

        groupActivityIds.forEach((activityId, activityIndex) => {
          if (activityIndex > 0) appendRelationship(groupActivityIds[activityIndex - 1], activityId);
        });
      });

      if (firstTaskIdForParent) parentFirstActivityId.set(parentKey, firstTaskIdForParent);
      if (lastTaskIdForParent) parentLastActivityId.set(parentKey, lastTaskIdForParent);
      if (anchorTaskIdForParent) parentAssignmentAnchorId.set(parentKey, anchorTaskIdForParent);
    });

    let hasExplicitDependencies = false;

    project.tasks.forEach((task, index) => {
      const currentParentKey = `PARENT_${index + 1}`;
      const successorTaskId = parentFirstActivityId.get(currentParentKey);
      if (!successorTaskId) return;

      const dependencyKeys = parseDependencies(task.dependencies);
      dependencyKeys.forEach((dependencyKey) => {
        const predecessorParentKey = taskLookupToParentKey.get(dependencyKey);
        const predecessorTaskId = predecessorParentKey ? parentLastActivityId.get(predecessorParentKey) : undefined;
        if (!predecessorTaskId) return;
        hasExplicitDependencies = true;
        appendRelationship(predecessorTaskId, successorTaskId);
      });
    });

    if (!hasExplicitDependencies) {
      project.tasks.forEach((_, index) => {
        if (index === 0) return;
        const predecessorTaskId = parentLastActivityId.get(`PARENT_${index}`);
        const successorTaskId = parentFirstActivityId.get(`PARENT_${index + 1}`);
        appendRelationship(predecessorTaskId, successorTaskId);
      });
    }

    const rsrcMap = new Map<string, string>();
    const rsrcNameByUuid = new Map<string, string>();
    let rsrcCounter = 1;

    const registerRsrc = (uuid?: string | null, displayName?: string | null) => {
      if (!uuid || rsrcMap.has(uuid)) return;
      const rsrcId = `R${String(rsrcCounter).padStart(4, '0')}`;
      rsrcMap.set(uuid, rsrcId);
      rsrcNameByUuid.set(uuid, displayName || `Resource ${rsrcCounter}`);
      rsrcCounter++;
    };

    assignments.forEach((assignment) => registerRsrc(assignment.resource_id, assignment.name || assignment.email || assignment.role));
    subtasks.forEach((subtask) => registerRsrc(subtask.resource_id, subtask.name || subtask.email || subtask.role));

    rsrcMap.forEach((rsrcId, uuid) => {
      const displayName = rsrcNameByUuid.get(uuid) || rsrcId;
      rsrcRows.push([rsrcId, displayName, displayName, 'Labor']);
    });

    assignments.forEach((assignment) => {
      const parentKey = taskLookupToParentKey.get(assignment.task_id);
      const taskId = parentKey ? parentAssignmentAnchorId.get(parentKey) : undefined;
      const rsrcId = rsrcMap.get(assignment.resource_id);
      const parentTask = parentKey ? parentTaskByKey.get(parentKey) : undefined;
      if (!taskId || !rsrcId || !parentTask) return;

      const progress = Math.max(0, Math.min(100, Math.round(parentTask.progress || 0)));
      const allocatedPct = Number(assignment.allocated_percentage);
      const allocatedHours = assignment.allocation_hrs != null ? Number(assignment.allocation_hrs) : Number.NaN;
      const taskHours = Math.max(0, (parentTask.duration || 0) * HOURS_PER_DAY);
      const targetQty = !Number.isNaN(allocatedHours) && allocatedHours > 0
        ? allocatedHours
        : taskHours * ((Number.isNaN(allocatedPct) ? 100 : allocatedPct) / 100);
      const remainQty = targetQty * (1 - progress / 100);
      const actQty = targetQty - remainQty;
      const rate = Number(assignment.rate) || 0;

      trRows.push([
        taskResourceUniqueId++,
        projectCode,
        taskId,
        rsrcId,
        round2(targetQty),
        round2(remainQty),
        round2(actQty),
        round2(targetQty * rate),
        round2(remainQty * rate),
        round2(actQty * rate),
        '',
        '',
      ]);
    });

    subtasks.forEach((subtask) => {
      const taskId = subtaskActivityIdBySubtaskId.get(subtask.id);
      const rsrcId = subtask.resource_id ? rsrcMap.get(subtask.resource_id) : undefined;
      if (!taskId || !rsrcId) return;

      const progress = subtaskProgress(subtask.status);
      const targetQty = Math.max(0, Number(subtask.estimated_hours) || 0);
      const remainQty = targetQty * (1 - progress / 100);
      const actQty = targetQty - remainQty;
      const startDate = formatPrimaveraDate(subtask.start_date);
      const endDate = formatPrimaveraDate(subtask.end_date);

      trRows.push([
        taskResourceUniqueId++,
        projectCode,
        taskId,
        rsrcId,
        round2(targetQty),
        round2(remainQty),
        round2(actQty),
        0,
        0,
        0,
        startDate,
        endDate,
      ]);
    });

    const wbsWs = XLSX.utils.aoa_to_sheet([wbsHeader, ...wbsRows]);
    wbsWs['!cols'] = [
      { wch: 14 },
      { wch: 24 },
      { wch: 24 },
      { wch: 44 },
      { wch: 18 },
      { wch: 10 },
    ];
    applyHeaderStyle(wbsWs, wbsHeader.length);
    XLSX.utils.book_append_sheet(wb, wbsWs, 'PROJWBS');

    const taskWs = XLSX.utils.aoa_to_sheet([taskHeader, ...taskRows]);
    taskWs['!cols'] = [
      { wch: 14 },
      { wch: 12 },
      { wch: 18 },
      { wch: 42 },
      { wch: 10 },
      { wch: 14 },
      { wch: 24 },
      { wch: 12 },
      { wch: 18 },
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 18 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
    ];
    applyHeaderStyle(taskWs, taskHeader.length);
    XLSX.utils.book_append_sheet(wb, taskWs, 'TASK');

    const predWs = XLSX.utils.aoa_to_sheet([predHeader, ...predRows]);
    predWs['!cols'] = [
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 12 },
      { wch: 12 },
    ];
    applyHeaderStyle(predWs, predHeader.length);
    XLSX.utils.book_append_sheet(wb, predWs, 'TASKPRED');

    const rsrcWs = XLSX.utils.aoa_to_sheet([rsrcHeader, ...rsrcRows]);
    rsrcWs['!cols'] = [
      { wch: 12 },
      { wch: 32 },
      { wch: 32 },
      { wch: 12 },
    ];
    applyHeaderStyle(rsrcWs, rsrcHeader.length);
    XLSX.utils.book_append_sheet(wb, rsrcWs, 'RSRC');

    const trWs = XLSX.utils.aoa_to_sheet([trHeader, ...trRows]);
    trWs['!cols'] = [
      { wch: 12 },
      { wch: 14 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 20 },
      { wch: 20 },
    ];
    applyHeaderStyle(trWs, trHeader.length);
    XLSX.utils.book_append_sheet(wb, trWs, 'TASKRSRC');

    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `${project.name}_Primavera_Export_${dateStr}.xlsx`);

    toast({
      title: 'Export Successful',
      description: `Exported ${project.tasks.length} tasks, ${subtasks.length} subtasks, ${wbsRows.length} WBS rows, ${predRows.length} relationships, ${trRows.length} assignments`,
    });
  } catch (error) {
    console.error('Primavera export error:', error);
    toast({ title: 'Export Error', description: 'Failed to export Primavera XLSX', variant: 'destructive' });
  }
};